import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${API_URL}/api`;

// Create axios instance
const api = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('profile');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  getMe: () => api.get('/auth/me'),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, newPassword) => api.post('/auth/reset-password', { token, new_password: newPassword }),
  verify2FA: (data) => api.post(`/auth/2fa/login?code=${encodeURIComponent(data.code)}&temp_token=${encodeURIComponent(data.temp_token)}`),
};

// Profile API
export const profileAPI = {
  getProfile: (userId) => api.get(`/profile/${userId}`),
  updateProfile: (data) => api.put('/profile', data),
};

// Documents API
export const documentsAPI = {
  upload: async (file, documentType, notes) => {
    const formData = new FormData();
    formData.append('file', file);
    
    // Create a separate axios request without the default Content-Type header
    // This allows the browser to set the correct multipart/form-data boundary
    const token = localStorage.getItem('token');
    
    try {
      const response = await axios.post(
        `${API}/documents/upload?document_type=${documentType}${notes ? `&notes=${encodeURIComponent(notes)}` : ''}`,
        formData,
        {
          headers: {
            'Authorization': token ? `Bearer ${token}` : '',
            // Don't set Content-Type - let axios/browser set it for FormData
          },
          timeout: 60000, // 60 second timeout for file uploads
        }
      );
      return response;
    } catch (error) {
      console.error('Upload error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      throw error;
    }
  },
  getMyDocuments: () => api.get('/documents'),
};

// Loads API
export const loadsAPI = {
  create: (data) => api.post('/loads', data),
  getAll: (params) => api.get('/loads', { params }),
  search: (filters) => {
    // Build params object, filtering out empty values
    const params = {
      limit: filters.limit || 50,
      skip: filters.skip || 0
    };
    
    if (filters.originCity) params.origin_city = filters.originCity;
    if (filters.originState) params.origin_state = filters.originState;
    if (filters.originRegion) params.origin_region = filters.originRegion;
    if (filters.originRadius) params.origin_radius = filters.originRadius;
    if (filters.destCity) params.dest_city = filters.destCity;
    if (filters.destState) params.dest_state = filters.destState;
    if (filters.destRegion) params.dest_region = filters.destRegion;
    if (filters.destRadius) params.dest_radius = filters.destRadius;
    if (filters.vehicleType) params.vehicle_type = filters.vehicleType;
    if (filters.loadCategory) params.load_category = filters.loadCategory;
    if (filters.minPrice) params.min_price = filters.minPrice;
    if (filters.maxPrice) params.max_price = filters.maxPrice;
    if (filters.status) params.status = filters.status;
    if (filters.verifiedOnly) params.verified_only = filters.verifiedOnly;
    if (filters.includeReverse) params.include_reverse = filters.includeReverse;
    if (filters.carrierLocation) params.carrier_location = filters.carrierLocation;
    
    return api.get('/loads', { params });
  },
  getOne: (id) => api.get(`/loads/${id}`),
  update: (id, data) => api.put(`/loads/${id}`, data),
  delete: (id) => api.delete(`/loads/${id}`),
  getHistory: (id) => api.get(`/loads/${id}/history`),
  getMyLoads: () => api.get('/my-loads'),
  updateStatus: (id, status, note) => api.put(`/loads/${id}/status?new_status=${status}${note ? `&note=${encodeURIComponent(note)}` : ''}`),
};

// Load Requests API
export const loadRequestsAPI = {
  create: (data) => api.post('/load-requests', data),
  getForLoad: (loadId) => api.get(`/load-requests/load/${loadId}`),
  approve: (requestId) => api.put(`/load-requests/${requestId}/approve`),
  decline: (requestId) => api.put(`/load-requests/${requestId}/decline`),
  getMyRequests: () => api.get('/my-requests'),
  getAssignedLoads: () => api.get('/assigned-loads'),
};

// Forum API
export const forumAPI = {
  createPost: (data) => api.post('/forum/posts', data),
  getPosts: (params) => api.get('/forum/posts', { params }),
  getPost: (id) => api.get(`/forum/posts/${id}`),
  updatePost: (id, data) => api.put(`/forum/posts/${id}`, data),
  deletePost: (id) => api.delete(`/forum/posts/${id}`),
  createReply: (postId, data) => api.post(`/forum/posts/${postId}/replies`, data),
  getReplies: (postId) => api.get(`/forum/posts/${postId}/replies`),
  reportPost: (postId, reason) => api.post(`/forum/posts/${postId}/report?reason=${encodeURIComponent(reason)}`),
};

// Notifications API
export const notificationsAPI = {
  getAll: (unreadOnly) => api.get('/notifications', { params: { unread_only: unreadOnly } }),
  markAsRead: (id) => api.put(`/notifications/${id}/read`),
  markAllRead: () => api.put('/notifications/read-all'),
  getUnreadCount: () => api.get('/notifications/count'),
};

// Ratings API
export const ratingsAPI = {
  create: (data) => api.post('/ratings', data),
  getUserRatings: (userId) => api.get(`/ratings/user/${userId}`),
  getUserSummary: (userId) => api.get(`/ratings/user/${userId}/summary`),
  getPending: () => api.get('/ratings/pending'),
};

// Saved Searches API
export const savedSearchesAPI = {
  create: (data) => api.post('/saved-searches', data),
  getAll: () => api.get('/saved-searches'),
  delete: (id) => api.delete(`/saved-searches/${id}`),
};

// Disputes API
export const disputesAPI = {
  create: (data) => api.post('/disputes', data),
  getAll: () => api.get('/disputes'),
  getOne: (id) => api.get(`/disputes/${id}`),
};

// Subscription API
export const subscriptionAPI = {
  getPlans: () => api.get('/subscription/plans'),
  getMySubscription: () => api.get('/subscription/my-subscription'),
  createCheckout: (planId, originUrl) => api.post('/subscription/checkout', null, {
    params: { plan_id: planId, origin_url: originUrl }
  }),
  getCheckoutStatus: (sessionId) => api.get(`/subscription/checkout-status/${sessionId}`),
};

// FMCSA Verification API
export const fmcsaAPI = {
  verify: (data) => api.post('/fmcsa/verify', data),
  getMyVerification: () => api.get('/fmcsa/my-verification'),
  getVerification: (userId) => api.get(`/fmcsa/verification/${userId}`),
  refresh: () => api.post('/fmcsa/refresh'),
  lookup: (dotNumber) => api.get(`/fmcsa/lookup/${dotNumber}`),
  // Admin endpoints
  adminList: (params) => api.get('/admin/fmcsa/verifications', { params }),
  adminVerify: (userId) => api.post(`/admin/fmcsa/verify/${userId}`),
};

// Admin API
export const adminAPI = {
  getUsers: (params) => api.get('/admin/users', { params }),
  verifyUser: (userId, data) => api.put(`/admin/users/${userId}/verify`, data),
  getDocuments: (status) => api.get('/admin/documents', { params: { status } }),
  reviewDocument: (documentId, data) => api.put(`/admin/documents/${documentId}/review`, data),
  moderatePost: (postId, status) => api.put(`/admin/forum/posts/${postId}/moderate?status=${status}`),
  getStats: () => api.get('/admin/stats'),
  getFMCSAVerifications: (params) => api.get('/admin/fmcsa/verifications', { params }),
  triggerFMCSAVerify: (userId) => api.post(`/admin/fmcsa/verify/${userId}`),
};

export default api;
